import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import { DEFAULT_BRANDING_NAME } from "@/modules/Settings/constants";
import type { Commit } from "vuex";
import {
  OrganisationBranding,
  OrganisationBrandingForm,
} from "@/types/organisatio.ts";

interface State {
  organisationBranding: OrganisationBranding;
  uploadedLogos: string[];
}

const state: State = {
  organisationBranding: {
    organisationID: "",
    logo: null,
    logo_dark: null,
    name: DEFAULT_BRANDING_NAME,
    inherited: null,
  },
  uploadedLogos: [],
};

const getters = {
  getOrganisationBranding: (state: State) => state.organisationBranding,
  getUploadedLogos: (state: State) => state.uploadedLogos,
};

const actions = {
  resetState({ commit }: { commit: Commit }) {
    commit("restoreDefaultBranding");
  },
  async uploadLogo(
    { commit }: { commit: Commit },
    {
      logo,
      logo_dark,
      organisationUuid,
    }: {
      logo?: File;
      logo_dark?: File;
      organisationUuid: string;
    },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const formData = new FormData();

    if (logo) {
      formData.append("logo", logo);
    }
    if (logo_dark) {
      formData.append("logo_dark", logo_dark);
    }

    formData.append("organisationID", organisationUuid);

    const response = await $http.post(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const uploadedLogo = response.data.logo || response.data.logo_dark;
    if (uploadedLogo) {
      commit("addToUploadedLogos", uploadedLogo);
    }

    return response.data;
  },
  async removeLogo(
    { commit }: { commit: Commit },
    {
      organisationUuid,
      logo,
    }: {
      organisationUuid: string;
      logo: string;
    },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    commit("removeFromUploadedLogos", logo);

    return await $http.delete(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload/${logo}?force=1`,
    );
  },
  async getBranding(
    { commit }: { commit: Commit },
    { organisationUuid }: { organisationUuid: string },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }
    commit("removeUploadedLogos");

    const response = await $http.get(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding`,
    );

    commit("setBranding", response.data);

    return response.data;
  },
  async getUploadedLogos(
    { commit }: { commit: Commit },
    { organisationUuid }: { organisationUuid: string },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.get(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload`,
    );

    commit("setUploadedLogos", response.data.logos);

    return response.data.logos;
  },
  async restoreName(
    { commit }: { commit: Commit },
    { organisationUuid }: { organisationUuid: string },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.post(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/name/reset`,
    );
    commit("restoreDefaultName");
    return response.data;
  },
  async restoreLogo(
    { commit }: { commit: Commit },
    { organisationUuid }: { organisationUuid: string },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.post(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/reset`,
    );
    commit("restoreDefaultLogo");
    commit("removeUploadedLogos");
    return response.data;
  },
  async saveBrandingName(
    _: unknown,
    { organisationUuid, name }: { organisationUuid: string; name: string },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const payload: Record<string, unknown> = {};

    if (name) {
      payload.name = name;
    }

    const response = await $http.put(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/name`,
      payload,
    );

    return response.data;
  },
  async saveBrandingLogo(
    _: unknown,
    {
      organisationUuid,
      form,
    }: { organisationUuid: string; form: OrganisationBrandingForm },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const payload: Record<string, unknown> = {};

    if (form.light_logo) {
      payload.logo = form.light_logo.startsWith("/")
        ? form.light_logo.slice(1)
        : form.light_logo;
    }
    if (form.dark_logo) {
      payload.logo_dark = form.dark_logo.startsWith("/")
        ? form.dark_logo.slice(1)
        : form.dark_logo;
    }

    const response = await $http.put(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo`,
      payload,
    );

    return response.data;
  },
  async removeUploadedLogos(
    { commit }: { commit: Commit },
    { organisationUuid }: { organisationUuid: string },
  ) {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.delete(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload`,
    );
    commit("restoreUploadedLogos");
    return response.data;
  },
};

const mutations = {
  setBranding: (state: State, data: OrganisationBranding) =>
    (state.organisationBranding = data),
  setUploadedLogos: (state: State, data: string[]) =>
    (state.uploadedLogos = data),
  addToUploadedLogos: (state: State, data: string) =>
    state.uploadedLogos.push(data),
  removeUploadedLogos: (state: State) => {
    state.uploadedLogos = [];
  },
  removeFromUploadedLogos: (state: State, data: string) => {
    // Extract the filename part from the full path
    const filename = data.split("/").pop(); // e.g. dark_icons8-chat-bubble-100.png
    if (!filename) return;

    // Get the prefix ("dark_" or "light_")
    const prefixMatch = filename.match(/^(dark_|light_)/);
    if (!prefixMatch) return;

    const prefix = prefixMatch[1]; // "dark_" or "light_"

    // Removal of all entries that have filename starting with the same prefix
    for (let i = state.uploadedLogos.length - 1; i >= 0; i--) {
      const file = state.uploadedLogos[i].split("/").pop();
      if (file?.startsWith(prefix)) {
        state.uploadedLogos.splice(i, 1);
      }
    }
  },
  restoreDefaultBranding: (state: State) => {
    state.organisationBranding = {
      organisationID: "",
      logo: null,
      logo_dark: null,
      name: DEFAULT_BRANDING_NAME,
      inherited: null,
    };
    state.uploadedLogos = [];
  },
  restoreDefaultName: (state: State) => {
    state.organisationBranding.name = DEFAULT_BRANDING_NAME;
  },
  restoreDefaultLogo: (state: State) => {
    state.organisationBranding.logo = null;
    state.organisationBranding.logo_dark = null;
    state.organisationBranding.inherited = null;
  },
  restoreUploadedLogos: (state: State) => {
    state.uploadedLogos = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
